import FormGenerator, { FormTypes } from '../../components/FormGenerator';
import Modal from '../../components/Modal';
import Manipulator from '../../utils/dom/manipulator';
import SelectorEngine from '../../utils/dom/selector-engine';
import Request, { ApiMode } from '../../utils/request';
import EventHandler from '../../utils/dom/event-handler';

const SELECTOR_YOUTH_ORDER_WRAPPER = '.p-youth-starter-offer';
const SELECTOR_YOUTH_ORDER_MODAL = '#js-youth-starter-offer-modal';

const SELECTOR_YOUTH_ORDER_QUESTIONS = '.p-youth-starter-offer-questions';

const KEY_OBJECT = 'fieldName';
const VALUE_OBJECT = 'value';
const HIDDEN_CLASS = 'u-hidden';
const IS_LOCAL = false;

const SINGLE_CHILD_COST = 4.99;
const CLICK_EVENT = 'click';

class YouthStarterOffer {

    get stepsData() {
		return this._stepsData;
	}

	set stepsData(_stepsData) {
		this._stepsData = _stepsData;
		if (_stepsData?.length > 0) {
			this.initiateFormGenerator(_stepsData);
		}
	}

	constructor() {
        this.element = SelectorEngine.findOne(SELECTOR_YOUTH_ORDER_WRAPPER);
        if(!this.element) {
            return;
        }
        this.init();
	}

	init() {
        this.modalElement = SelectorEngine.findOne(SELECTOR_YOUTH_ORDER_MODAL, this.element);
        this.youthStarterOfferQuestion = SelectorEngine.findOne(SELECTOR_YOUTH_ORDER_QUESTIONS, this.element);
		this.successModal = SelectorEngine.findOne('.p-youth-starter-offer-success', this.element);
		
		this.stepsEndpoint = Manipulator.getDataAttribute(this.element, 'steps-endpoint');
		this.submitEndpoint = Manipulator.getDataAttribute(this.element, 'submit-endpoint');

		this.submitNotificationEndpoint = Manipulator.getDataAttribute(this.element, 'notification-endpoint');
		this.submitNotificationBtn = SelectorEngine.findOne('.p-youth-starter-offer__holding-page-notification-btn', this.element);
		this.notificationContainer = SelectorEngine.findOne('.p-youth-starter-offer__holding-page-notification', this.element);
		this.notificationSuccess = SelectorEngine.findOne('.p-youth-starter-offer__holding-page-notification-success', this.element);

		this.submitNotificationErrorContainer = SelectorEngine.findOne('.p-youth-starter-offer__holding-page-error', this.element);
		this.submitNotificationErrorContainerBtn = SelectorEngine.findOne('.p-youth-starter-offer__holding-page-error-btn', this.element);

		EventHandler.on(this.submitNotificationBtn, CLICK_EVENT, () => {
			this.submitNotification();
		});

		EventHandler.on(this.submitNotificationErrorContainerBtn, CLICK_EVENT, () => {
			this.submitNotificationErrorContainer.classList.add("u-hidden");
		});

		// As per ticket #30303 we need to show a notification page if no stock is available, BE will toggle u-hidden class on pageload, Here we check for the class and load the fetch API only if u-hidden is not present. 
		if(!this.modalElement.classList.contains("u-hidden")) {
			this.modal = new Modal(this.modalElement, { backdrop: 'static' });
			this.costPerChild = Manipulator.getDataAttribute(this.element, 'cost-per-child') ?? SINGLE_CHILD_COST;
			this.modal.show(); 
			this.fetchYouthStarterOfferSteps();
		}       
	}

    async fetchYouthStarterOfferSteps() {
		try {
			const url = this.stepsEndpoint;
			const response = await Request.get(
				url,
				{},
				{
					local: IS_LOCAL,
					req: {
						apiMode: ApiMode.LtaError
					}
				}
			);
			const { steps, extraParams, error } = response;

			if(extraParams) {
				this.extraParams = extraParams;
			}

			if (steps?.length) {
				this.stepsData = steps;
			}

			if (!!error?.hasError) {
				throw error;
			}
		} catch (error) {
			if (error.hasError) {
				console.log('Error :', error);
			}
		}
	}

	youthStarterOfferSubmitHandler(payload) {
		return Request.post(this.submitEndpoint, payload,
			{
				local: IS_LOCAL,
				req: {
					apiMode: ApiMode.LtaError
				}
			}
		);
	}

    initiateFormGenerator(stepsData) {
		this.formGenerator = new FormGenerator(this.youthStarterOfferQuestion, {
			questions: stepsData,
			totalSteps: 5,
			hasSeparator: true,
			formType: FormTypes.StepForm,
			themeColor: 'color-lta-accent-orange',
            stepVisibility: [true, true, false, true, true],
			isBackButtonEnabled: true,
			submitButton: { title: 'Pay now' },
			wrapperClasses: ['p-youth-start-offer__fixed-width', 'p-youth-start-offer__fixed-width', 'p-youth-start-offer__fixed-width', 'p-youth-start-offer__fixed-width', 'p-youth-start-offer__full-width'],
			checkoutSuccess: this.checkoutSuccess.bind(this),
			saveAddress: false,
			userDetails: {
				name: this.extraParams?.name,
				email: this.extraParams?.email
			}
		});

		this.setTotalCost(0, 0);
		
		this.formGenerator.onStepChange((cuurrentStep) => {
			const nextButtonWrapper = SelectorEngine.findOne('.lta-form-generator__form-next', this._element);
			const payload = this.prepareRequestObject(this.formGenerator.answers);
			const totalChildren = payload?.children?.length;
			const totalCost = this.setTotalCost(this.costPerChild, totalChildren);
			const checkoutQuestionRef = this.formGenerator.questions.find(question => question.questionType === 'checkout')?.ref;
			checkoutQuestionRef.data = {
				...checkoutQuestionRef.data,
				count: totalChildren,
				cost: this.costPerChild,
				total: totalCost
			}
			if(cuurrentStep === 5) {
				nextButtonWrapper.classList.add(HIDDEN_CLASS);
				checkoutQuestionRef.initPayment();
			} else {
				nextButtonWrapper.classList.remove(HIDDEN_CLASS);
			}

		});
		
        this.formGenerator.onSubmit(async (answer) => {
			this.checkoutSuccess();
        });
    }

    prepareRequestObject(stepsAnswers) {
		let answerList = {};
		for (const stepKey in stepsAnswers) {
			const stepAnswersObjects = stepsAnswers[stepKey];
			for (const questionKey in stepAnswersObjects) {
				const answerObject = stepAnswersObjects[questionKey];
				const formatObject = {};
				formatObject[answerObject[KEY_OBJECT]] = answerObject[VALUE_OBJECT];
				answerList = {
					...answerList,
					...formatObject
				};
			}
		}
		if(answerList.address) {
			answerList.address = {
				"mailingStreet" : answerList.address?.street,
				"postCode" : answerList.address?.postCode,
				"mailingCity" : answerList.address?.city,
				"county" : answerList.address?.state,
				"addressCountry" : "United Kingdom"
			}
		}
		const children = [{
			"childFirstName" : answerList?.childOneFirstName,
			"childLastName" : answerList?.childOneLastName,
			"childDateOfBirth" : answerList?.childOneDateOfBirth,
			"childGender" : answerList?.childOneGender
		}];
		delete answerList.childOneFirstName;
		delete answerList.childOneLastName;
		delete answerList.childOneDateOfBirth;
		delete answerList.childOneGender;
		
		if(answerList.totalChildren === "Two") {
			children.push({
				"childFirstName" : answerList?.childTwoFirstName,
				"childLastName" : answerList?.childTwoLastName,
				"childDateOfBirth" : answerList?.childTwoDateOfBirth,
				"childGender" : answerList?.childTwoGender
			});
			delete answerList.childTwoFirstName;
			delete answerList.childTwoLastName;
			delete answerList.childTwoDateOfBirth;
			delete answerList.childTwoGender;
		}
		delete answerList.totalChildren;
		answerList.source = "Youth Starter";
		answerList.children = children;
		if(this.extraParams) {
			answerList = {
				...answerList,
				...this.extraParams
			}
		}
		
		return answerList;
	}

	async submitNotification() {
		try {
			const responseData = await Request.post(this.submitNotificationEndpoint);
			const { success } = responseData;
			if (success) {
				this.notificationSuccess.classList.remove("u-hidden");
				this.notificationContainer.classList.add("u-hidden");
			} else {
				this.submitNotificationErrorContainer.classList.remove("u-hidden");
			}
		} catch {
			this.submitNotificationErrorContainer.classList.remove("u-hidden");
		}
	}

	async checkoutSuccess(afterAuthentication) {
		try {
			const payload = this.prepareRequestObject(this.formGenerator.answers);

			const data = await this.youthStarterOfferSubmitHandler({ 
				...payload, 
				IS_REQ_AUTHORIZATION_SUCCESS: afterAuthentication 
			});				
			
			if((data.Success || data.success) && data.Status !== "REQ_AUTHORIZATION") {
				this.successModal.classList.remove(HIDDEN_CLASS);
				this.modal.hide();
			} else {
				// TODO: Display Error Message on screen
				console.log('Error :', data);
			}
			return data;
		} catch (error) {
			console.log('Error :', error);
			return { Success: false, success: false, message: error.message || "Something went wrong" };
		}
	}

	setTotalCost(cost, totalChildren) {
		window.lta.lta_payment = window.lta?.lta_payment || {}
		window.lta.lta_payment.basket = window.lta.lta_payment?.basket || {}
		window.lta.lta_payment.basket.totalCost = cost * totalChildren;
		return cost * totalChildren;
	}
}

const globalYouthStarterOffer = new YouthStarterOffer();

export default globalYouthStarterOffer;

